<template>
  <div
    class="w-full h-full flex flex-col justify-center"
    :class="{
      'drop-zone cursor-pointer': fullDropZone,
      'dragging-over': isDraggingOverDropZone,
    }"
    @dragenter.prevent="isDraggingOverDropZone = true"
    @dragover.prevent="isDraggingOverDropZone = true"
    @dragleave.prevent="isDraggingOverDropZone = false"
    @drop.prevent="onFileDrop"
    @click="onDropZonePress('main-container')"
  >
    <input
      type="file"
      ref="hidden-file-input"
      :accept="acceptedFileTypes"
      class="absolute w-0 invisible"
      @change="onFileInputChange"
    />
    <div
      class="flex justify-center py-60 px-20 cursor-pointer mobile:py-40"
      :class="{
        'drop-zone': !fullDropZone,
        'dragging-over': isDraggingOverDropZone,
      }"
      @click="onDropZonePress('drop-zone-container')"
    >
      <div class="inline-block text-center">
        <div class="inline-flex items-center justify-center h-70 w-70 p-10 mb-20 bg-brand-excel shadow-2dp rounded-full"
        >
          <FontAwesomeIcon
            :icon="['fad', 'file-spreadsheet']"
            class="text-4xl text-white"
          />
        </div>
        <p class="text-lg text-primary mobile:text-base">
          <span class="font-bold">Drop</span>
          or
          <span class="font-bold">Press</span>
          here to select your guest list.
        </p>
      </div>
    </div>
    <div
      class="text-center mb-40 px-40 mobile:px-10 mobile:mb-30"
      :class="{ 'mt-40': !fullDropZone }"
    >
      <h2 class="text-2xl text-primary mb-10 mobile:text-xl">Need help formatting the file?</h2>
      <p class="text-base text-primary leading-snug mb-20 mobile:text-base">
        We prepared a template file showing you the supported columns
      </p>
      <a
        :href="excelTemplateSrc"
        download
        class="inline-flex items-center justify-between text-white pl-30 bg-primary shadow-3dp transition-shadow
        duration-200 ease-in-out-quart rounded-5 overflow-hidden hover:shadow-8dp"
        @click.stop
      >
        <span class="text-base pr-25">
          Download template
        </span>
        <span class="py-14 px-20 bg-primary-dark">
          <FontAwesomeIcon
            :icon="['far', 'arrow-to-bottom']"
            class="text-xl text-white"
          />
        </span>
      </a>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { supportedImportFileTypes } from '@/config/appConfig';

export default {
  props: {
    fullDropZone: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isDraggingOverDropZone: false,
    acceptedFileTypes: supportedImportFileTypes,
    // Static template shipped with the app (public/files), served from the same origin so
    // the `download` attribute always yields the spreadsheet and never an HTML page
    excelTemplateSrc: `${process.env.BASE_URL}files/eventor-guests-import-template.xlsx`,
  }),
  methods: {
    ...mapActions('guest/import', {
      updateSelectedFiles: 'updateSelectedFiles',
      updateGuestImportDialogIsVisible: 'updateDialogIsVisible',
    }),
    onFileDrop(e) {
      const acceptedFileTypes = this.acceptedFileTypes.split(',');
      const fileExtension = `.${e.dataTransfer.files[0]?.name.split('.').pop()}`;

      if (!acceptedFileTypes.includes(fileExtension)) {
        this.$alert('Your file type is invalid, select an Excel or CSV file and try again', 'Wrong file type', {
          confirmButtonText: 'Ok',
          closeOnClickModal: true,
          closeOnPressEscape: true,
        }).catch((error) => error);
      } else {
        this.isDraggingOverDropZone = false;
        this.updateSelectedFiles(e.dataTransfer.files);
      }
    },
    onFileInputChange(e) {
      this.updateSelectedFiles(e.target.files);
      this.updateGuestImportDialogIsVisible(true);
    },
    onDropZonePress(origin) {
      if ((origin === 'main-container' && !this.fullDropZone)
        || (origin === 'drop-zone-container' && this.fullDropZone)) return;

      this.$refs['hidden-file-input'].click();
    },
  },
};
</script>

<style scoped>
.drop-zone {
  @apply border-2 border-gray-500 border-dashed rounded-5;

  transition: background 0.25s easeInOutQuart, border 0.25s easeInOutQuart;

  &:hover {
    @apply bg-gray-300 border-primary;
  }

  &.dragging-over {
    @apply bg-gray-200 border-2 border-brand-excel;
  }
}
</style>
